<template>
  <div class="deck-share">
    <h2 class="title is-4">Share this Slide Deck</h2>
    <p class="m-b-30">
      Share your <a :href="shareUrl" class="share-link" target="_blank">deck's preview page</a> to let anyone see an overview and create a copy in their own account.
    </p>

    <div class="referral-form">
      <b-field>
        <b-taginput
          v-model="inviteEmails"
          :disabled="(!isOwner || !localIsPublic) || sendingInvites"
          ellipsis
          icon="user"
          icon-pack="fas"
          placeholder="Emails, comma separated"
          aria-close-label="Remove user"
        />
      </b-field>

      <div v-if="localIsPublic" class="actions">
        <button @click.stop.prevent="copyText(shareUrl)" class="button is-light">
          Copy link
        </button>
        <button @click.stop.prevent="inviteFriends" :disabled="(!isOwner || !localIsPublic) || sendingInvites" class="button is-primary">
          Send
        </button>
      </div>
    </div>

    <div class="notification is-one-line is-small is-transparent">
      <p class="is-size-7">
        🎁 You can earn credits by referring friends, <a :href="'/account/refer'" class="is-small">learn more here</a>
      </p>
    </div>

    <b-field label="Visibility">
      <b-switch v-model="localIsPublic" @input="setPublicOrPrivate($event)" :disabled="!isOwner">{{ localIsPublic ? 'Public (anyone with the link can view)' : 'This deck is private (not shareable)' }}</b-switch>
    </b-field>

    <p v-if="!localIsPublic" class="notification is-one-line is-small is-light">
      <i class="fas fa-info-circle is-small is-size-6 m-r-5" />
      <span class="is-size-6">Set this deck to public to create a share link</span>
    </p>
  </div>
</template>

<script>
import { setPublicOrPrivate } from '~/queries/deckQueries.gql'
import { uuidToShort } from '~/common/uuid-shortner'

export default {
  props: ['deckId', 'ownerId', 'isPublic', 'deckName'],
  data () {
    return {
      localIsPublic: false,
      inviteEmails: [],
      sendingInvites: false
    }
  },
  computed: {
    id () {
      return this.deckId || this.$route.params.id
    },
    shareUrl () {
      return `${process.env.SITE_URL}/preview/${this.id}?trf=${uuidToShort(this.$auth.user.id)}&source=deck-share-modal`
    },
    isOwner () {
      return this.$auth.user.id === this.ownerId
    },
    currentUser () {
      return this.$store.state.user
    }
  },
  created () {
    this.localIsPublic = this.isPublic
  },
  methods: {
    async setPublicOrPrivate (val) {
      if (!this.isOwner) {
        this.$buefy.toast.open('Only the owner can change deck sharing')
        return
      }
      await this.$apollo.mutate({
        mutation: setPublicOrPrivate,
        variables: {
          id: this.id,
          isPublic: val
        }
      })
      this.$emit('refresh:decks')
    },
    copyText (text) {
      const input = document.createElement('input')
      input.value = text
      document.body.appendChild(input)
      input.select()
      document.execCommand('Copy')
      input.remove()
      this.$buefy.toast.open({ message: 'copied!', type: 'is-primary' })
      this.posthogCapture('referral:deck_share_link_copy')
    },

    async inviteFriends () {
      this.sendingInvites = true
      for (const email of this.inviteEmails) {
        await this.sendInvite(email)
      }
      this.$buefy.toast.open({ message: 'Invites sent!', type: 'is-primary' })
      this.posthogCapture('referral:deck_share_send', { invites: this.inviteEmails.length })
      this.inviteEmails = []
      this.sendingInvites = false
    },

    async sendInvite (email) {
      const emailTemplate = {
        to: email,
        templateId: 41164540,
        tag: 'share-deck',
        variables: {
          user: {
            name: this.currentUser.displayName,
            email: this.currentUser.email
          },
          emailToInvite: email,
          deckName: this.deckName,
          deckNumber: this.id,
          trackingParam: `?trf=${uuidToShort(this.$auth.user.id)}&source=deck-share-modal`
        }
      }

      try {
        const resp = await this.$axios.post(
          process.env.FUNC_URL + '/send-email-template',
          emailTemplate
        )
        this.log('EMAIL SENT', resp)
        // emailSent = true
      } catch (e) {
        this.log('error sending email message', e.message, e)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.deck-share {
  background-color: white;
  padding: 40px;
  border-radius: 6px;

  .share-link {
    text-decoration: underline;
    color: inherit;
  }
}

.notification.is-one-line.is-small.is-transparent {
  margin-top: 0;
  background-color: transparent;
  padding-left: 0px;
}

.referral-form {
  display: flex;
  flex-flow: column;
  gap: 0.25em;

  .field {
    flex: 1;
    margin-bottom: 0;
  }

  .actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5em;
  }
}
</style>
